import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";

function SalePackageItemRow({ pkgId, item, idx, excluded, displayPrice, mergedLabel, onToggle }) {
  const isOptional = !!item.optional;
  const isStruck = excluded;
  const price = displayPrice ?? (Number(item.price) || 0);
  return (
    <li
      data-testid={`sale-pkg-item-${pkgId}-${idx}`}
      className="flex items-center justify-between gap-2 text-[12px]"
    >
      <label
        className={`flex items-center gap-2 min-w-0 ${
          isOptional && !mergedLabel ? "cursor-pointer" : "cursor-default"
        }`}
      >
        <input
          type="checkbox"
          checked={!isStruck}
          disabled={!isOptional || !!mergedLabel}
          onChange={() => onToggle(pkgId, item.label)}
          data-testid={`sale-pkg-item-toggle-${pkgId}-${idx}`}
          className="accent-ink shrink-0"
        />
        <span
          className={`truncate ${isStruck ? "line-through text-ink-subtle" : "text-ink"}`}
        >
          {item.label}
          {mergedLabel && (
            <span className="ml-2 font-mono text-[9px] uppercase tracking-wider text-ink-subtle">
              {mergedLabel}
            </span>
          )}
          {!isOptional && !mergedLabel && (
            <span className="ml-2 font-mono text-[9px] uppercase tracking-wider text-ink-subtle">
              mandatory
            </span>
          )}
        </span>
      </label>
      <span
        className={`font-mono shrink-0 ${
          isStruck ? "line-through text-ink-subtle" : "text-ink-muted"
        }`}
      >
        {fmtMoney(price)}
      </span>
    </li>
  );
}

function SalePackageRow({ pkg, active, exclusions, gstType, finalPrice, onTogglePackage, onToggleItem }) {
  const items = pkg.items || [];

  // For inter mode, precompute the CGST amount that gets folded into IGST.
  const cgstAmount = gstType === "inter"
    ? (items.find((it) => it.label === "CGST")?.price || 0)
    : 0;

  const decorateItem = (it) => {
    if (gstType !== "inter") return {};
    if (it.label === "CGST") {
      return { excluded: true, mergedLabel: "merged into IGST" };
    }
    if (it.label === "IGST" && cgstAmount > 0) {
      return {
        displayPrice: (Number(it.price) || 0) + cgstAmount,
        mergedLabel: "incl. CGST · inter",
      };
    }
    return {};
  };

  return (
    <div
      data-testid={`sale-pkg-row-${pkg.id}`}
      className={`border-b border-canvas-line last:border-b-0 ${
        active ? "bg-canvas-panel" : ""
      }`}
    >
      <label className="flex items-center justify-between gap-3 cursor-pointer px-3 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <input
            type="checkbox"
            checked={active}
            onChange={() => onTogglePackage(pkg.id)}
            data-testid={`sale-pkg-toggle-${pkg.id}`}
            className="accent-ink shrink-0"
          />
          <div className="min-w-0">
            <div className="text-sm font-medium text-ink truncate">{pkg.name}</div>
            {pkg.description && (
              <div className="text-[11px] text-ink-muted truncate">{pkg.description}</div>
            )}
          </div>
        </div>
        <span className="font-mono text-xs font-semibold text-ink shrink-0">
          + {fmtMoney(finalPrice)}
        </span>
      </label>
      {active && items.length > 0 && (
        <ul className="pl-9 pr-3 pb-2 space-y-1">
          {items.map((it, idx) => {
            const deco = decorateItem(it);
            return (
              <SalePackageItemRow
                key={`${pkg.id}-${it.label}-${idx}`}
                pkgId={pkg.id}
                item={it}
                idx={idx}
                excluded={
                  deco.excluded ?? (!!it.optional && exclusions.includes(it.label))
                }
                displayPrice={deco.displayPrice}
                mergedLabel={deco.mergedLabel}
                onToggle={onToggleItem}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
}
function SelectorBody({ loading, packages, selected, onToggle }) {
  if (loading) {
    return (
      <div className="border border-canvas-line p-4 font-mono text-xs text-ink-subtle text-center">
        Loading packages…
      </div>
    );
  }
  if (packages.length === 0) {
    return (
      <div className="border border-canvas-line p-4 font-mono text-xs text-ink-subtle text-center">
        No active packages. Create one from the Packages page.
      </div>
    );
  }
  return (
    <div className="border border-canvas-line max-h-48 overflow-y-auto" data-testid="packages-selector">
      {packages.map((p) => {
        const active = selected.includes(p.id);
        return (
          <label
            key={p.id}
            data-testid={`pkg-toggle-${p.id}`}
            className={`flex items-center justify-between gap-3 px-3 py-2 border-b border-canvas-line last:border-b-0 cursor-pointer ${
              active ? "bg-canvas-panel" : "hover:bg-canvas-panel"
            }`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <input
                type="checkbox"
                checked={active}
                onChange={() => onToggle(p.id)}
                className="accent-ink shrink-0"
              />
              <span className="text-sm text-ink truncate">{p.name}</span>
            </div>
            <span className="font-mono text-xs font-semibold text-ink shrink-0">
              {fmtMoney(p.price)}
            </span>
          </label>
        );
      })}
    </div>
  );
}

export function PackagesSelector({ selected = [], onChange, branchId, onAvailableIdsChange }) {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Without a branch we cannot resolve a branch type, so show nothing —
    // packages are strictly scoped per branch type.
    if (!branchId) {
      setPackages([]);
      setLoading(false);
      if (onAvailableIdsChange) onAvailableIdsChange([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get("/packages", { params: { active_only: true, branch_id: branchId } })
      .then((r) => {
        if (cancelled) return;
        const list = r.data || [];
        setPackages(list);
        if (onAvailableIdsChange) onAvailableIdsChange(list.map((p) => p.id));
      })
      .catch(() => {
        if (!cancelled) {
          setPackages([]);
          if (onAvailableIdsChange) onAvailableIdsChange([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  };

  if (!branchId) {
    return (
      <div>
        <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
          Available Packages
        </label>
        <div
          className="border border-canvas-line p-4 font-mono text-xs text-ink-subtle text-center"
          data-testid="packages-selector-needs-branch"
        >
          Select a branch first to load matching packages.
        </div>
      </div>
    );
  }

  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
        Available Packages ({selected.length} attached)
      </label>
      <SelectorBody
        loading={loading}
        packages={packages}
        selected={selected}
        onToggle={toggle}
      />
    </div>
  );
}

/**
 * Sale-time package picker.
 *  - Each selected package shows its items.
 *  - Mandatory items are always included.
 *  - Optional items have a checkbox; unticking removes their price from the sale total.
 *  - exclusions: { [package_id]: [item_label, ...] }
 */
export function SalePackagesPicker({
  vehicleId,
  availableIds = [],
  selected = [],
  exclusions = {},
  gstType = "intra",
  onChange,
  onTotalChange,
}) {
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    if (!availableIds.length) {
      setPackages((prev) => (prev.length ? [] : prev));
      return;
    }
    let cancelled = false;
    api
      .get("/packages")
      .then((r) => {
        if (cancelled) return;
        const filtered = (r.data || []).filter((p) => availableIds.includes(p.id));
        setPackages(filtered);
      })
      .catch(() => {
        if (!cancelled) setPackages([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId, availableIds.join(",")]);

  // Inter mode: CGST amount is merged into IGST (single 18% line). Total stays the same.
  const computeFinal = (pkg, ex) => {
    const items = pkg.items || [];
    if (!items.length) return Number(pkg.price) || 0;
    // In both modes the running total equals the package price minus any excluded optional items.
    // For inter we don't subtract CGST — it stays in the sum but reflected on the IGST line.
    return items.reduce((acc, it) => {
      const isExcluded = it.optional && (ex || []).includes(it.label);
      return isExcluded ? acc : acc + (Number(it.price) || 0);
    }, 0);
  };

  const recompute = (nextSelected, nextExclusions) => {
    const sum = packages
      .filter((p) => nextSelected.includes(p.id))
      .reduce((acc, p) => acc + computeFinal(p, nextExclusions[p.id] || []), 0);
    const selectedPkgs = packages.filter((p) => nextSelected.includes(p.id));
    if (onTotalChange) onTotalChange(sum, selectedPkgs, nextExclusions);
  };

  // When gstType flips, the total changes — propagate to parent.
  useEffect(() => {
    recompute(selected, exclusions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gstType, packages]);

  const togglePackage = (id) => {
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    const nextExclusions = { ...exclusions };
    if (!next.includes(id)) delete nextExclusions[id];
    onChange(next, nextExclusions);
    recompute(next, nextExclusions);
  };

  const toggleItem = (pkgId, itemLabel) => {
    const current = exclusions[pkgId] || [];
    const nextList = current.includes(itemLabel)
      ? current.filter((l) => l !== itemLabel)
      : [...current, itemLabel];
    const nextExclusions = { ...exclusions, [pkgId]: nextList };
    if (nextList.length === 0) delete nextExclusions[pkgId];
    onChange(selected, nextExclusions);
    recompute(selected, nextExclusions);
  };

  if (packages.length === 0) return null;

  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
        Add-on Packages ({selected.length} selected)
      </label>
      <div className="border border-canvas-line" data-testid="sale-packages-picker">
        {packages.map((p) => (
          <SalePackageRow
            key={p.id}
            pkg={p}
            active={selected.includes(p.id)}
            exclusions={exclusions[p.id] || []}
            gstType={gstType}
            finalPrice={computeFinal(p, exclusions[p.id] || [])}
            onTogglePackage={togglePackage}
            onToggleItem={toggleItem}
          />
        ))}
      </div>
    </div>
  );
}
